import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Check, X, ArrowRight, ArrowLeft, FileText } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

const CATEGORIES = ["EB-1A", "EB-2 NIW", "O-1", "Not sure — advise me"];

const initial = {
  full_name: "",
  email: "",
  phone: "",
  country_of_citizenship: "",
  current_country: "",
  visa_category: "",
  field_of_expertise: "",
  highest_degree: "",
  years_experience: "",
  publications_count: "",
  citations_count: "",
  awards: "",
  case_summary: "",
  timeline: "",
};

export default function CaseReview() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initial);
  const [reviewId, setReviewId] = useState(null);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fee, setFee] = useState("200");

  useEffect(() => {
    api.get("/config/public").then((r) => setFee(r.data.case_review_fee || "200")).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const stepValid = () => {
    if (step === 0) return form.full_name && form.email && form.country_of_citizenship && form.current_country;
    if (step === 1) return form.visa_category && form.field_of_expertise && form.case_summary && form.case_summary.length >= 60;
    return true;
  };

  const submitBasics = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        years_experience: form.years_experience ? Number(form.years_experience) : null,
        publications_count: form.publications_count ? Number(form.publications_count) : null,
        citations_count: form.citations_count ? Number(form.citations_count) : null,
      };
      const r = await api.post("/case-reviews", payload);
      setReviewId(r.data.id);
      setStep(2);
    } catch (e) {
      toast.error("Could not create submission. Please retry.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileChange = async (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length || !reviewId) return;
    setUploading(true);
    for (const f of list) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post(`/case-reviews/${reviewId}/upload`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setFiles((prev) => [...prev, { name: r.data.original_filename, size: r.data.size, file_id: r.data.file_id }]);
        toast.success(`${r.data.original_filename} uploaded`);
      } catch (err) {
        toast.error(`Upload failed: ${f.name}`);
      }
    }
    setUploading(false);
    e.target.value = "";
  };

  const proceedToPayment = async () => {
    try {
      const r = await api.get(`/case-reviews/${reviewId}/payment-url`);
      if (r.data.configured && r.data.url) {
        window.location.href = r.data.url;
      } else {
        // Payment not yet configured — show confirmation screen
        nav(`/case-review/success?ref=${reviewId}&pending=1`);
      }
    } catch {
      toast.error("Unable to load payment link. Please try again.");
    }
  };

  return (
    <div data-testid="case-review-page" className="min-h-screen">
      <section className="container-x pt-16 pb-8">
        <div className="max-w-4xl">
          <div className="eyebrow">Initial Case Review · ${fee}</div>
          <h1 className="font-display text-4xl lg:text-5xl mt-4 tracking-tight text-[#0F172A] leading-tight">
            Tell us about your case.
          </h1>
          <p className="mt-6 text-lg text-[#4A4D50] max-w-2xl leading-relaxed">
            A senior attorney will return a written strategy within 5 business days. All information is
            confidential and does not create an attorney-client relationship until an engagement letter is signed.
          </p>
        </div>
      </section>

      {/* Progress bar */}
      <section className="container-x">
        <div className="grid grid-cols-4 gap-2 mb-12">
          {["Contact", "Case Details", "Documents", "Payment"].map((label, i) => (
            <div key={label} data-testid={`step-indicator-${i}`} className="flex flex-col gap-2">
              <div className={`h-[3px] w-full ${i <= step ? "bg-[#0F172A]" : "bg-[#0F172A]/15"}`} />
              <div className={`text-xs font-mono-plex uppercase tracking-widest ${i === step ? "text-[#0F172A]" : "text-[#4A4D50]"}`}>
                {String(i + 1).padStart(2, "0")} · {label}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="container-x pb-24">
        <div className="bg-white border border-[#0F172A]/10 p-8 md:p-14">
          {step === 0 && (
            <div data-testid="step-contact" className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Field label="Full name*" testid="input-full-name" value={form.full_name} onChange={(v) => set("full_name", v)} />
              <Field label="Email*" type="email" testid="input-email" value={form.email} onChange={(v) => set("email", v)} />
              <Field label="Phone (optional)" testid="input-phone" value={form.phone} onChange={(v) => set("phone", v)} />
              <Field label="Country of citizenship*" testid="input-citizenship" value={form.country_of_citizenship} onChange={(v) => set("country_of_citizenship", v)} />
              <Field label="Current country of residence*" testid="input-residence" value={form.current_country} onChange={(v) => set("current_country", v)} />
            </div>
          )}

          {step === 1 && (
            <div data-testid="step-details" className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <label className="eyebrow">Visa category*</label>
                <div className="mt-3">
                  <Select value={form.visa_category} onValueChange={(v) => set("visa_category", v)}>
                    <SelectTrigger data-testid="select-visa-category" className="editorial-input !h-auto !border-b !border-t-0 !border-l-0 !border-r-0 !rounded-none !bg-transparent focus:!ring-0 !px-0">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => (<SelectItem key={c} value={c} data-testid={`option-${c}`}>{c}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Field label="Field of expertise*" testid="input-field" value={form.field_of_expertise} onChange={(v) => set("field_of_expertise", v)} placeholder="e.g. Machine Learning, Molecular Biology" />
              <Field label="Highest degree" testid="input-degree" value={form.highest_degree} onChange={(v) => set("highest_degree", v)} placeholder="e.g. PhD, MS, BFA" />
              <Field label="Years of experience" type="number" testid="input-years" value={form.years_experience} onChange={(v) => set("years_experience", v)} />
              <Field label="Publications count" type="number" testid="input-publications" value={form.publications_count} onChange={(v) => set("publications_count", v)} />
              <Field label="Citations count" type="number" testid="input-citations" value={form.citations_count} onChange={(v) => set("citations_count", v)} />
              <div className="md:col-span-2">
                <label className="eyebrow">Awards / recognitions (optional)</label>
                <Textarea data-testid="input-awards" value={form.awards} onChange={(e) => set("awards", e.target.value)} rows={3} className="editorial-input mt-3 !border-b !border-t-0 !border-l-0 !border-r-0 !rounded-none !bg-transparent focus-visible:!ring-0" />
              </div>
              <div className="md:col-span-2">
                <label className="eyebrow">Case summary* (min. 60 characters)</label>
                <Textarea
                  data-testid="input-summary"
                  value={form.case_summary}
                  onChange={(e) => set("case_summary", e.target.value)}
                  rows={6}
                  className="editorial-input mt-3 !border-b !border-t-0 !border-l-0 !border-r-0 !rounded-none !bg-transparent focus-visible:!ring-0"
                  placeholder="Describe your work, its significance, and what you'd like to achieve."
                />
                <div className="mt-2 text-xs font-mono-plex text-[#4A4D50]">{form.case_summary.length} chars</div>
              </div>
              <Field label="Desired timeline" testid="input-timeline" value={form.timeline} onChange={(v) => set("timeline", v)} placeholder="e.g. File within 3 months" />
            </div>
          )}

          {step === 2 && (
            <div data-testid="step-documents">
              <div className="eyebrow">Documents</div>
              <h2 className="font-display text-2xl mt-3 text-[#0F172A]">Upload your evidence</h2>
              <p className="mt-3 text-[#4A4D50] max-w-2xl leading-relaxed">
                CV, degree certificates, top publications, award letters, press coverage. PDF, DOC, DOCX, JPG, PNG. Max 25MB per file.
                Optional at this stage — you may proceed to payment without uploads.
              </p>
              <label
                data-testid="file-drop-area"
                className="mt-8 flex flex-col items-center justify-center border border-dashed border-[#0F172A]/25 p-16 cursor-pointer hover:border-[#0F172A] transition-colors"
              >
                <Upload size={26} className="text-[#C2A679]" strokeWidth={1.5} />
                <div className="mt-4 font-medium text-[#0F172A]">Click or drag files here</div>
                <div className="mt-1 text-xs text-[#4A4D50] font-mono-plex uppercase tracking-widest">
                  {uploading ? "Uploading…" : "PDF · DOC · DOCX · JPG · PNG"}
                </div>
                <input
                  data-testid="file-input"
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt,.rtf"
                  className="hidden"
                  onChange={handleFileChange}
                />
              </label>
              {files.length > 0 && (
                <ul className="mt-8 divide-y divide-[#0F172A]/10 border border-[#0F172A]/10 bg-[#F7F5F0]">
                  {files.map((f, i) => (
                    <li key={i} data-testid={`file-${i}`} className="flex items-center justify-between p-4">
                      <div className="flex items-center gap-3">
                        <FileText size={18} className="text-[#0F172A]" strokeWidth={1.5} />
                        <div>
                          <div className="text-sm font-medium text-[#0F172A]">{f.name}</div>
                          <div className="text-xs text-[#4A4D50] font-mono-plex">{(f.size / 1024).toFixed(1)} KB</div>
                        </div>
                      </div>
                      <Check size={18} className="text-[#1A3626]" />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {step === 3 && (
            <div data-testid="step-payment">
              <div className="eyebrow">Payment</div>
              <h2 className="font-display text-3xl mt-3 text-[#0F172A]">Case Review Fee — ${fee}</h2>
              <p className="mt-4 text-[#4A4D50] max-w-2xl leading-relaxed">
                You will be redirected to our secure LawPay hosted payment page to complete payment.
                Once received, a senior attorney will begin your written case review and respond within 5 business days.
              </p>
              <div className="mt-10 bg-[#F7F5F0] border border-[#0F172A]/10 p-8 max-w-lg">
                <div className="flex items-baseline justify-between">
                  <span className="eyebrow">Initial Case Review</span>
                  <span className="font-display text-3xl text-[#0F172A]">${fee}</span>
                </div>
                <div className="mt-6 pt-6 border-t border-[#0F172A]/10 text-sm text-[#4A4D50] leading-relaxed">
                  Written strategy · Best-fit category recommendation · Evidence gap analysis · No hidden fees.
                </div>
              </div>
              <div className="mt-10">
                <button data-testid="pay-button" onClick={proceedToPayment} className="btn-primary">
                  Continue to LawPay <ArrowRight size={18} />
                </button>
                <div className="mt-4 text-xs font-mono-plex text-[#4A4D50]">
                  Payment is processed by LawPay (registered legal-industry processor). We never see your card details.
                </div>
              </div>
            </div>
          )}

          {/* Nav */}
          <div className="mt-12 pt-8 border-t border-[#0F172A]/10 flex items-center justify-between">
            <button
              data-testid="back-button"
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={step === 0}
              className={`btn-secondary ${step === 0 ? "opacity-40 cursor-not-allowed" : ""}`}
            >
              <ArrowLeft size={16} /> Back
            </button>
            {step < 3 && step !== 2 && (
              <button
                data-testid="next-button"
                disabled={!stepValid() || submitting}
                onClick={() => {
                  if (step === 1) submitBasics();
                  else setStep((s) => s + 1);
                }}
                className={`btn-primary ${!stepValid() || submitting ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                {step === 1 ? (submitting ? "Saving…" : "Save & continue") : "Continue"} <ArrowRight size={16} />
              </button>
            )}
            {step === 2 && (
              <button data-testid="proceed-payment-button" onClick={() => setStep(3)} className="btn-primary">
                Continue to Payment <ArrowRight size={16} />
              </button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid, placeholder }) {
  return (
    <div>
      <label className="eyebrow">{label}</label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="editorial-input mt-3"
      />
    </div>
  );
}
